import { TsRestClient } from '@eluve/api-client-provider';
import { SessionUnavailableError } from '@eluve/eluve-errors';
import { Logger } from '@eluve/logger-main';
import { getMetadataFromFileName } from '@eluve/utils';

import { ResolveEmptySegmentEvent, TaskHandler } from '../types';

/**
 * [ENG-4498] Resolve a recording segment that reserved a backend upload slot
 * (`PreparingRecording` → `user_uploads PENDING_UPLOAD` + `transcripts
 * PENDING_AUDIO`) but whose local file was never created — the device-sleep /
 * dead-worklet phantom that permanently blocks note generation.
 *
 * Two-step resolution, GCS truth first:
 *
 * 1. `resolveReservedUpload` reconciles the slot against stored bytes. The
 *    backend recovers the slot if a usable object exists (a PUT that landed
 *    before `markFileUploadComplete` was lost), so this can never discard a
 *    successfully-uploaded recording.
 * 2. [ENG-4709] When the backend reports `left_pending` (no GCS object), the
 *    slot is cancelled via `cancelOrphanedFile(trigger: 'empty_segment')`.
 *    This client is the authority here: it reserved the slot, observed that no
 *    local file was ever created, and GCS was just confirmed empty — there are
 *    no bytes anywhere to lose, only a pending row that would block note
 *    generation until the periodic-sync reconciler happens to catch it.
 */
export const resolveEmptySegmentHandler = ({
  client,
  logger,
}: {
  client: TsRestClient | null;
  logger: Logger;
}): TaskHandler<ResolveEmptySegmentEvent> => {
  return async (task, context) => {
    if (!client) {
      logger.warn('API client is not available');
      throw new SessionUnavailableError('API client not initialized');
    }

    // The reserved user_upload shares the segment's stable id, derived the same
    // way the reservation (prepare-audio-records) and markChunkFinal do.
    const { databaseFileId: fileId } = getMetadataFromFileName({
      userId: task.userId,
      fileName: task.fileName,
    });

    const response = await client.appointments.resolveReservedUpload({
      params: {
        tenantId: context.tenantId,
        appointmentId: task.appointmentId,
        fileId,
      },
      body: {},
      fetchOptions: { signal: context.signal },
    });

    if (response?.status === 401) {
      throw new SessionUnavailableError(
        'Session expired while resolving empty segment',
      );
    }

    if (response?.status === 403) {
      logger.warn('Appointment not accessible while resolving empty segment', {
        appointmentId: task.appointmentId,
        segmentId: task.segmentId,
        fileId,
      });
      return;
    }

    if (response?.status === 404) {
      // The reserved slot is not visible to the caller. Nothing to resolve —
      // do not retry forever; treat as terminal.
      logger.warn('Reserved upload not found while resolving empty segment', {
        appointmentId: task.appointmentId,
        segmentId: task.segmentId,
        fileId,
      });
      return;
    }

    if (response?.status !== 200) {
      logger.warn('Failed to resolve empty segment', {
        appointmentId: task.appointmentId,
        segmentId: task.segmentId,
        fileId,
        status: response?.status,
      });
      throw new Error('Failed to resolve empty segment');
    }

    const { outcome, previousStatus } = response.body;
    const logContext = {
      appointmentId: task.appointmentId,
      segmentId: task.segmentId,
      fileId,
      outcome,
      previousStatus,
      suspensionGapMs: task.suspensionGapMs,
    };

    // For recovered / cancelled / already-terminal outcomes the slot is settled
    // server-side and the task is terminal — retrying won't change GCS truth.
    if (outcome !== 'left_pending') {
      logger.info('Resolved reserved upload for empty segment', logContext);
      return;
    }

    // `left_pending` is the core empty-segment phantom: no finalized GCS object
    // exists, and the reserved row would block note generation indefinitely.
    // Escalate to an authoritative cancel — the GCS check above just proved
    // there are no bytes to lose. [ENG-4709]
    const cancelResponse = await client.appointments.cancelOrphanedFile({
      params: {
        tenantId: context.tenantId,
        appointmentId: task.appointmentId,
        fileId,
      },
      body: { trigger: 'empty_segment' },
      fetchOptions: { signal: context.signal },
    });

    if (cancelResponse?.status === 401) {
      throw new SessionUnavailableError(
        'Session expired while cancelling empty-segment phantom slot',
      );
    }

    if (cancelResponse?.status === 403) {
      logger.warn(
        'Appointment not accessible while cancelling empty-segment phantom slot',
        logContext,
      );
      return;
    }

    if (cancelResponse?.status === 404) {
      // Slot no longer visible to the caller — nothing left to cancel.
      logger.warn(
        'Phantom slot not found while cancelling empty segment',
        logContext,
      );
      return;
    }

    if (cancelResponse?.status === 409) {
      // The row left PENDING_UPLOAD between the resolve above and this cancel —
      // something else settled it (e.g. the periodic-sync reconciler, or an
      // upload that finalized concurrently). Terminal: the slot is no longer
      // pending, which is the outcome we wanted.
      logger.info('Phantom slot settled concurrently — cancel skipped', {
        ...logContext,
        currentStatus: cancelResponse.body.currentStatus,
      });
      return;
    }

    if (cancelResponse?.status !== 200) {
      // Tripwire: a phantom slot is still PENDING_UPLOAD and this attempt could
      // not clear it. Throw so the task processor retries; the periodic-sync
      // reconciler remains the backstop if retries exhaust.
      logger.error('Failed to cancel empty-segment phantom slot', {
        ...logContext,
        cancelStatus: cancelResponse?.status,
      });
      throw new Error('Failed to cancel empty-segment phantom slot');
    }

    logger.info('Cancelled empty-segment phantom slot', {
      ...logContext,
      wasAlreadyCancelled: cancelResponse.body.wasAlreadyCancelled,
      cancelPreviousStatus: cancelResponse.body.previousStatus,
    });
  };
};
